<template>
  <article
    class="question-card"
    :style="{
      '--accent-color':
        backgroundColors[
          colorIndex % backgroundColors.length
        ]
    }"
    @click="emit('click')"
  >

    <!-- =====================================================
         ACCENT
    ====================================================== -->

    <div class="card-accent"></div>


    <!-- =====================================================
         HEADER
    ====================================================== -->

    <header class="card-header">

      <div class="question-type">
        <span class="question-type-dot"></span>

        Вопрос
      </div>


      <div class="header-actions">

        <button
          type="button"
          class="like-button"
          :class="{
            active: isLiked
          }"
          :aria-label="
            isLiked
              ? 'Убрать лайк'
              : 'Поставить лайк'
          "
          @click.stop="toggleLike"
        >
          <svg
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              d="M12 20.25C12 20.25 4 15.7 4 9.4C4 6.5 5.95 4.75 8.25 4.75C10.05 4.75 11.25 5.7 12 6.85C12.75 5.7 13.95 4.75 15.75 4.75C18.05 4.75 20 6.5 20 9.4C20 15.7 12 20.25 12 20.25Z"
              fill="currentColor"
              stroke="currentColor"
              stroke-width="1.45"
              stroke-linejoin="round"
            />
          </svg>

          <span
            v-if="displayLikesCount > 0"
            class="like-count"
          >
            {{ displayLikesCount }}
          </span>
        </button>

      </div>

    </header>


    <!-- =====================================================
         QUESTION
    ====================================================== -->

    <div class="question-content">

      <h2 class="title">
        {{ question.title || "Без названия" }}
      </h2>

      <p class="description">
        {{
          question.description
          || "Описание вопроса отсутствует."
        }}
      </p>

    </div>


    <!-- =====================================================
         AUTHOR
    ====================================================== -->

    <div class="creator-block">

      <div class="creator-avatar-wrapper">

        <img
          v-if="question.creatorImageURL"
          :src="question.creatorImageURL"
          :alt="question.creatorName || 'Автор'"
          class="creator-avatar"
        />

        <div
          v-else
          class="creator-avatar creator-avatar-placeholder"
        >
          {{ creatorInitial }}
        </div>

        <span class="creator-online-dot"></span>

      </div>


      <div class="creator-info">

        <span class="creator-label">
          Автор вопроса
        </span>

        <div class="creator-name">
          {{
            question.creatorName
            || "Пользователь"
          }}
        </div>

        <div
          v-if="question.creatorSpecifics"
          class="creator-role"
        >
          {{ question.creatorSpecifics }}
        </div>

      </div>


      <div class="author-chevron">
        <svg
          viewBox="0 0 20 20"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M7.5 5.5L12 10L7.5 14.5"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>

    </div>


    <!-- =====================================================
         FOOTER
    ====================================================== -->

    <footer class="card-footer">

      <!-- ANSWERS -->

      <div class="answers-info">

        <div
          v-if="visibleAnswers.length"
          class="responders"
        >

          <div
            v-for="(answer, index) in visibleAnswers"
            :key="
              answer.id
              || answer.responderID
              || index
            "
            class="responder-avatar-wrapper"
            :style="{
              '--avatar-index': index
            }"
          >

            <img
              v-if="answer.responderImageURL"
              :src="answer.responderImageURL"
              :alt="answer.responderName || 'Участник'"
              class="responder-avatar"
            />

            <div
              v-else
              class="responder-avatar responder-placeholder"
            >
              {{
                responderInitial(
                  answer.responderName
                )
              }}
            </div>

          </div>


          <div
            v-if="extraAnswersCount > 0"
            class="more-answers"
          >
            +{{ extraAnswersCount }}
          </div>

        </div>


        <div
          v-else
          class="no-answers-icon"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M6 6.5H18C19.1 6.5 20 7.4 20 8.5V14.5C20 15.6 19.1 16.5 18 16.5H12L8 19V16.5H6C4.9 16.5 4 15.6 4 14.5V8.5C4 7.4 4.9 6.5 6 6.5Z"
              stroke="currentColor"
              stroke-width="1.6"
            />
          </svg>
        </div>


        <span class="answers-label">
          {{ answersTitle }}
        </span>

      </div>


      <!-- COMMENTS -->

      <div class="comments">

        <div class="comment-icon-wrapper">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M6 5H18C19.66 5 21 6.34 21 8V14C21 15.66 19.66 17 18 17H12L7 20V17H6C4.34 17 3 15.66 3 14V8C3 6.34 4.34 5 6 5Z"
              stroke="currentColor"
              stroke-width="1.55"
              stroke-linecap="round"
              stroke-linejoin="round"
            />

            <path
              d="M7.5 9.5H16.5"
              stroke="currentColor"
              stroke-width="1.45"
              stroke-linecap="round"
            />

            <path
              d="M7.5 13H13"
              stroke="currentColor"
              stroke-width="1.45"
              stroke-linecap="round"
            />
          </svg>
        </div>

        <span class="comment-count">
          {{ commentsTitle }}
        </span>

      </div>

    </footer>


    <!-- =====================================================
         HOVER ARROW
    ====================================================== -->

    <div class="open-question">

      <span>
        Открыть
      </span>

      <svg
        viewBox="0 0 20 20"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M6 10H14"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
        />

        <path
          d="M11 7L14 10L11 13"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>

    </div>

  </article>
</template>


<script setup>

import {
  computed,
  ref
} from "vue";

import QuestionService
  from "../api/QuestionService.js";


/* ============================================================
   PROPS
============================================================ */

const props =
  defineProps({
    question: {
      type: Object,
      required: true
    },

    colorIndex: {
      type: Number,
      default: 0
    }
  });


/* ============================================================
   EMITS
============================================================ */

const emit =
  defineEmits([
    "click"
  ]);


/* ============================================================
   COLORS
============================================================ */

const backgroundColors = [
  "#6655D9",
  "#3A9F72",
  "#E46A46",
  "#D89B32",
  "#388BD5"
];


/* ============================================================
   LIKE
============================================================ */

const isLiked =
  ref(
    Boolean(
      props.question?.isLiked
    )
  );


const localLikesOffset =
  ref(0);


const displayLikesCount =
  computed(() => {

    const serverCount =
      Number(
        props.question?.likesCount
        ?? 0
      );

    return Math.max(
      0,
      serverCount
      + localLikesOffset.value
    );

  });


const toggleLike =
  async () => {

    const previousState =
      isLiked.value;


    /*
     * Оптимистичное обновление UI.
     */
    isLiked.value =
      !previousState;


    localLikesOffset.value +=
      isLiked.value
        ? 1
        : -1;


    try {

      await QuestionService
        .updateStat({
          docId:
            props.question.id,

          collection:
            "Questions",

          field:
            "likesCount",

          operation:
            isLiked.value
              ? "increment"
              : "decrement"
        });


      console.log(
        `👍 Лайк ${
          isLiked.value
            ? "добавлен"
            : "удалён"
        } для ${
          props.question.id
        }`
      );


    } catch (error) {

      console.error(
        "❌ Ошибка при лайке:",
        error?.message
        || error
      );


      /*
       * Откатываем состояние.
       */
      isLiked.value =
        previousState;


      localLikesOffset.value +=
        previousState
          ? 1
          : -1;

    }

  };


/* ============================================================
   ANSWERS
============================================================ */

const allAnswers =
  computed(
    () =>
      Array.isArray(
        props.question?.answers
      )
        ? props.question.answers
        : []
  );


const visibleAnswers =
  computed(
    () =>
      allAnswers.value.slice(
        0,
        3
      )
  );


const extraAnswersCount =
  computed(
    () =>
      Math.max(
        0,
        allAnswers.value.length
        - visibleAnswers.value.length
      )
  );


const answersTitle =
  computed(() => {

    const count =
      allAnswers.value.length;


    if (count === 0) {
      return "Ответов пока нет";
    }


    const lastTwo =
      count % 100;

    const lastOne =
      count % 10;


    if (
      lastTwo >= 11
      && lastTwo <= 19
    ) {
      return `${count} ответов`;
    }


    if (
      lastOne === 1
    ) {
      return `${count} ответ`;
    }


    if (
      lastOne >= 2
      && lastOne <= 4
    ) {
      return `${count} ответа`;
    }


    return `${count} ответов`;

  });


/* ============================================================
   COMMENTS
============================================================ */

const commentsTitle =
  computed(() => {

    const count =
      Number(
        props.question?.commentCount
        ?? 0
      );


    const lastTwo =
      count % 100;

    const lastOne =
      count % 10;


    if (
      lastTwo >= 11
      && lastTwo <= 19
    ) {
      return `${count} комментариев`;
    }


    if (
      lastOne === 1
    ) {
      return `${count} комментарий`;
    }


    if (
      lastOne >= 2
      && lastOne <= 4
    ) {
      return `${count} комментария`;
    }


    return `${count} комментариев`;

  });


/* ============================================================
   AVATARS
============================================================ */

const creatorInitial =
  computed(() => {

    const name =
      props.question?.creatorName
      || "?";

    return name
      .trim()
      .charAt(0)
      .toUpperCase();

  });


const responderInitial =
  name => {

    if (!name) {
      return "?";
    }

    return String(name)
      .trim()
      .charAt(0)
      .toUpperCase();

  };

</script>


<style scoped>

/* ============================================================
   CARD
============================================================ */

.question-card {
  position: relative;

  width: 100%;
  height: 100%;
  min-height: 285px;

  display: flex;
  flex-direction: column;

  box-sizing: border-box;

  padding:
    20px
    20px
    17px;

  overflow: hidden;

  color: #303640;

  background:
    linear-gradient(
      145deg,
      rgba(255, 255, 255, 0.98),
      rgba(251, 252, 254, 0.96)
    );

  border:
    1px solid
    rgba(30, 48, 73, 0.075);

  border-radius: 22px;

  box-shadow:
    0 10px 30px
    rgba(20, 38, 65, 0.055);

  cursor: pointer;

  isolation: isolate;

  transition:
    transform
    0.24s
    cubic-bezier(
      0.22,
      1,
      0.36,
      1
    ),
    box-shadow
    0.24s ease,
    border-color
    0.24s ease;
}


/*
 * Очень лёгкое цветное свечение,
 * зависящее от colorIndex.
 */
.question-card::before {
  content: "";

  position: absolute;

  top: -90px;
  right: -70px;

  z-index: -1;

  width: 220px;
  height: 220px;

  pointer-events: none;

  background:
    radial-gradient(
      circle,
      color-mix(
        in srgb,
        var(--accent-color) 12%,
        transparent
      ),
      transparent 68%
    );

  border-radius: 50%;

  transition:
    transform
    0.35s ease,
    opacity
    0.35s ease;
}


.question-card::after {
  content: "";

  position: absolute;

  left: 18px;
  right: 18px;
  bottom: -35px;

  z-index: -1;

  height: 60px;

  pointer-events: none;

  background:
    color-mix(
      in srgb,
      var(--accent-color) 7%,
      transparent
    );

  filter:
    blur(30px);

  opacity: 0.75;
}


.question-card:hover {
  transform:
    translateY(-5px);

  border-color:
    color-mix(
      in srgb,
      var(--accent-color) 22%,
      rgba(30, 48, 73, 0.08)
    );

  box-shadow:
    0 19px 44px
    rgba(24, 47, 82, 0.105);
}


.question-card:hover::before {
  transform:
    scale(1.12)
    translate(
      -5px,
      4px
    );
}


/* ============================================================
   ACCENT
============================================================ */

.card-accent {
  position: absolute;

  top: 0;
  left: 24px;
  right: 24px;

  height: 3px;

  background:
    linear-gradient(
      90deg,
      transparent,
      var(--accent-color),
      transparent
    );

  border-radius:
    0
    0
    20px
    20px;

  opacity: 0.78;
}


/* ============================================================
   HEADER
============================================================ */

.card-header {
  min-height: 31px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 12px;
}


.question-type {
  display: inline-flex;
  align-items: center;

  gap: 6px;

  padding:
    5px
    8px;

  color:
    color-mix(
      in srgb,
      var(--accent-color) 82%,
      #343b46
    );

  background:
    color-mix(
      in srgb,
      var(--accent-color) 7%,
      #ffffff
    );

  border:
    1px solid
    color-mix(
      in srgb,
      var(--accent-color) 10%,
      transparent
    );

  border-radius: 999px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 7px;

  letter-spacing:
    0.04em;

  text-transform:
    uppercase;
}


.question-type-dot {
  width: 5px;
  height: 5px;

  background:
    var(--accent-color);

  border-radius: 50%;

  box-shadow:
    0 0 0 3px
    color-mix(
      in srgb,
      var(--accent-color) 10%,
      transparent
    );
}


.header-actions {
  display: flex;
  align-items: center;

  gap: 8px;
}


/* ============================================================
   LIKE
============================================================ */

.like-button {
  min-width: 34px;
  height: 34px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  gap: 5px;

  padding:
    0
    9px;

  color: #a2a8b1;

  background:
    rgba(52, 70, 98, 0.04);

  border:
    1px solid
    rgba(35, 55, 85, 0.055);

  border-radius: 11px;

  cursor: pointer;

  transition:
    color 0.18s ease,
    background-color 0.18s ease,
    border-color 0.18s ease,
    transform 0.18s ease;
}


.like-button:hover {
  transform:
    scale(1.04);

  color: #e26872;

  background:
    rgba(224, 91, 105, 0.07);

  border-color:
    rgba(224, 91, 105, 0.10);
}


.like-button.active {
  color: #dd5b69;

  background:
    rgba(224, 91, 105, 0.075);

  border-color:
    rgba(224, 91, 105, 0.11);
}


.like-button svg {
  width: 15px;
  height: 15px;

  /*
   * По умолчанию сердце только
   * контурное визуально за счёт opacity.
   */
  opacity: 0.72;

  transition:
    transform 0.2s ease,
    opacity 0.2s ease;
}


.like-button.active svg {
  opacity: 1;

  animation:
    likePulse
    0.32s
    cubic-bezier(
      0.34,
      1.56,
      0.64,
      1
    );
}


@keyframes likePulse {

  0% {
    transform:
      scale(0.75);
  }

  70% {
    transform:
      scale(1.18);
  }

  100% {
    transform:
      scale(1);
  }

}


.like-count {
  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 8px;
}


/* ============================================================
   QUESTION CONTENT
============================================================ */

.question-content {
  margin-top: 13px;
}


.title {
  display: -webkit-box;

  margin: 0;

  overflow: hidden;

  color: #303640;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 16px;

  line-height: 1.32;

  letter-spacing: -0.15px;

  text-overflow:
    ellipsis;

  -webkit-box-orient:
    vertical;

  -webkit-line-clamp:
    2;
}


.description {
  display: -webkit-box;

  margin:
    8px
    0
    0;

  overflow: hidden;

  color: #858d98;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 10px;

  line-height: 1.55;

  text-overflow:
    ellipsis;

  -webkit-box-orient:
    vertical;

  -webkit-line-clamp:
    3;
}


/* ============================================================
   CREATOR
============================================================ */

.creator-block {
  display: flex;
  align-items: center;

  gap: 10px;

  margin-top: 17px;

  padding:
    11px
    12px;

  background:
    rgba(71, 89, 116, 0.035);

  border:
    1px solid
    rgba(34, 55, 84, 0.045);

  border-radius: 15px;

  transition:
    background-color
    0.18s ease,
    border-color
    0.18s ease;
}


.question-card:hover
.creator-block {
  background:
    color-mix(
      in srgb,
      var(--accent-color) 4%,
      #f8f9fb
    );

  border-color:
    color-mix(
      in srgb,
      var(--accent-color) 9%,
      rgba(34, 55, 84, 0.045)
    );
}


.creator-avatar-wrapper {
  position: relative;

  width: 38px;
  height: 38px;

  flex-shrink: 0;
}


.creator-avatar {
  width: 38px;
  height: 38px;

  display: block;

  box-sizing: border-box;

  object-fit: cover;

  border:
    2px solid
    rgba(255, 255, 255, 0.95);

  border-radius: 12px;

  box-shadow:
    0 4px 11px
    rgba(25, 45, 75, 0.10);
}


.creator-avatar-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;

  color:
    var(--accent-color);

  background:
    color-mix(
      in srgb,
      var(--accent-color) 9%,
      #ffffff
    );

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 13px;
}


.creator-online-dot {
  position: absolute;

  right: -2px;
  bottom: -2px;

  width: 9px;
  height: 9px;

  box-sizing: border-box;

  background: #42b97a;

  border:
    2px solid
    #ffffff;

  border-radius: 50%;
}


.creator-info {
  min-width: 0;

  flex: 1;

  display: flex;
  flex-direction: column;

  gap: 2px;
}


.creator-label {
  color: #a3a9b2;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 6px;

  letter-spacing:
    0.055em;

  text-transform:
    uppercase;
}


.creator-name {
  overflow: hidden;

  color: #414751;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 10px;

  line-height: 1.25;

  text-overflow:
    ellipsis;

  white-space:
    nowrap;
}


.creator-role {
  overflow: hidden;

  color: #9198a2;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 8px;

  text-overflow:
    ellipsis;

  white-space:
    nowrap;
}


.author-chevron {
  width: 27px;
  height: 27px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #afb4bc;

  background:
    rgba(255, 255, 255, 0.75);

  border:
    1px solid
    rgba(35, 55, 85, 0.045);

  border-radius: 9px;

  transition:
    color 0.18s ease,
    transform 0.18s ease,
    background-color 0.18s ease;
}


.author-chevron svg {
  width: 13px;
  height: 13px;
}


.question-card:hover
.author-chevron {
  color:
    var(--accent-color);

  background: #ffffff;

  transform:
    translateX(2px);
}


/* ============================================================
   FOOTER
============================================================ */

.card-footer {
  min-height: 37px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 12px;

  margin-top: auto;

  padding-top: 15px;
}


.answers-info {
  min-width: 0;

  display: flex;
  align-items: center;

  gap: 8px;
}


.responders {
  display: flex;
  align-items: center;

  flex-shrink: 0;
}


.responder-avatar-wrapper {
  position: relative;

  margin-left:
    calc(
      var(--avatar-index)
      * -7px
    );

  z-index:
    calc(
      10
      - var(--avatar-index)
    );
}


.responder-avatar-wrapper:first-child {
  margin-left: 0;
}


.responder-avatar {
  width: 25px;
  height: 25px;

  display: flex;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;

  object-fit: cover;

  background: #edf0f4;

  border:
    2px solid
    #ffffff;

  border-radius: 50%;

  box-shadow:
    0 3px 8px
    rgba(28, 47, 75, 0.09);
}


.responder-placeholder {
  color:
    color-mix(
      in srgb,
      var(--accent-color) 75%,
      #515965
    );

  background:
    color-mix(
      in srgb,
      var(--accent-color) 9%,
      #f5f7fa
    );

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 7px;
}


.more-answers {
  width: 25px;
  height: 25px;

  display: flex;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;

  margin-left: -6px;

  color: #818995;

  background: #f2f4f7;

  border:
    2px solid
    #ffffff;

  border-radius: 50%;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 6px;
}


.no-answers-icon {
  width: 25px;
  height: 25px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #a1a8b2;

  background:
    rgba(70, 90, 120, 0.045);

  border-radius: 8px;
}


.no-answers-icon svg {
  width: 14px;
  height: 14px;
}


.answers-label {
  overflow: hidden;

  color: #8d949e;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 7px;

  text-overflow:
    ellipsis;

  white-space:
    nowrap;
}


/* ============================================================
   COMMENTS
============================================================ */

.comments {
  flex-shrink: 0;

  display: flex;
  align-items: center;

  gap: 6px;
}


.comment-icon-wrapper {
  width: 25px;
  height: 25px;

  display: flex;
  align-items: center;
  justify-content: center;

  color:
    color-mix(
      in srgb,
      var(--accent-color) 66%,
      #7d8794
    );

  background:
    color-mix(
      in srgb,
      var(--accent-color) 6%,
      #f7f8fa
    );

  border-radius: 8px;
}


.comment-icon-wrapper svg {
  width: 13px;
  height: 13px;
}


.comment-count {
  color: #898f99;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 7px;

  white-space:
    nowrap;
}


/* ============================================================
   OPEN
============================================================ */

.open-question {
  position: absolute;

  right: 18px;
  bottom: 13px;

  display: flex;
  align-items: center;

  gap: 4px;

  pointer-events: none;

  color:
    var(--accent-color);

  opacity: 0;

  transform:
    translateX(-5px);

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 7px;

  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}


.open-question svg {
  width: 12px;
  height: 12px;
}


.question-card:hover
.open-question {
  opacity: 1;

  transform:
    translateX(0);
}


/*
 * При hover подпись комментариев
 * немного скрываем, чтобы освободить
 * место под "Открыть".
 */
.question-card:hover
.comments {
  opacity: 0;

  transition:
    opacity 0.15s ease;
}


/* ============================================================
   DARK
============================================================ */

html[data-theme="dark"]
.question-card {
  color: #edf0f4;

  background:
    linear-gradient(
      145deg,
      rgba(36, 38, 44, 0.98),
      rgba(31, 33, 38, 0.98)
    );

  border-color:
    rgba(255, 255, 255, 0.065);

  box-shadow:
    0 11px 31px
    rgba(0, 0, 0, 0.18);
}


html[data-theme="dark"]
.question-card:hover {
  border-color:
    color-mix(
      in srgb,
      var(--accent-color) 25%,
      rgba(255, 255, 255, 0.07)
    );

  box-shadow:
    0 18px 43px
    rgba(0, 0, 0, 0.30);
}


html[data-theme="dark"]
.question-type {
  color:
    color-mix(
      in srgb,
      var(--accent-color) 65%,
      #ffffff
    );

  background:
    color-mix(
      in srgb,
      var(--accent-color) 11%,
      #24262b
    );

  border-color:
    color-mix(
      in srgb,
      var(--accent-color) 15%,
      transparent
    );
}


html[data-theme="dark"]
.title {
  color: #f0f1f4;
}


html[data-theme="dark"]
.description {
  color: #969da7;
}


html[data-theme="dark"]
.like-button {
  color: #858c96;

  background:
    rgba(255, 255, 255, 0.035);

  border-color:
    rgba(255, 255, 255, 0.05);
}


html[data-theme="dark"]
.like-button.active {
  color: #ef7b86;

  background:
    rgba(220, 91, 105, 0.09);

  border-color:
    rgba(225, 100, 112, 0.12);
}


html[data-theme="dark"]
.creator-block {
  background:
    rgba(255, 255, 255, 0.027);

  border-color:
    rgba(255, 255, 255, 0.045);
}


html[data-theme="dark"]
.question-card:hover
.creator-block {
  background:
    color-mix(
      in srgb,
      var(--accent-color) 6%,
      #24262b
    );

  border-color:
    color-mix(
      in srgb,
      var(--accent-color) 12%,
      rgba(255, 255, 255, 0.04)
    );
}


html[data-theme="dark"]
.creator-avatar {
  border-color: #292b31;
}


html[data-theme="dark"]
.creator-avatar-placeholder {
  color:
    color-mix(
      in srgb,
      var(--accent-color) 62%,
      #ffffff
    );

  background:
    color-mix(
      in srgb,
      var(--accent-color) 12%,
      #292b31
    );
}


html[data-theme="dark"]
.creator-online-dot {
  border-color: #292b31;
}


html[data-theme="dark"]
.creator-label {
  color: #6e7580;
}


html[data-theme="dark"]
.creator-name {
  color: #dddddf;
}


html[data-theme="dark"]
.creator-role {
  color: #808792;
}


html[data-theme="dark"]
.author-chevron {
  color: #747b86;

  background:
    rgba(255, 255, 255, 0.035);

  border-color:
    rgba(255, 255, 255, 0.045);
}


html[data-theme="dark"]
.question-card:hover
.author-chevron {
  color:
    color-mix(
      in srgb,
      var(--accent-color) 60%,
      #ffffff
    );

  background:
    rgba(255, 255, 255, 0.05);
}


html[data-theme="dark"]
.responder-avatar {
  background: #34363c;

  border-color: #24262b;
}


html[data-theme="dark"]
.responder-placeholder {
  color:
    color-mix(
      in srgb,
      var(--accent-color) 58%,
      #ffffff
    );

  background:
    color-mix(
      in srgb,
      var(--accent-color) 10%,
      #33353b
    );
}


html[data-theme="dark"]
.more-answers {
  color: #9a9fa8;

  background: #33353b;

  border-color: #24262b;
}


html[data-theme="dark"]
.no-answers-icon {
  color: #777f8a;

  background:
    rgba(255, 255, 255, 0.035);
}


html[data-theme="dark"]
.answers-label,
html[data-theme="dark"]
.comment-count {
  color: #777f89;
}


html[data-theme="dark"]
.comment-icon-wrapper {
  color:
    color-mix(
      in srgb,
      var(--accent-color) 58%,
      #ffffff
    );

  background:
    color-mix(
      in srgb,
      var(--accent-color) 7%,
      #292b31
    );
}


/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 600px) {

  .question-card {
    min-height: 280px;

    padding:
      17px
      16px
      15px;

    border-radius: 19px;
  }


  .card-accent {
    left: 20px;
    right: 20px;
  }


  .title {
    font-size: 15px;

    -webkit-line-clamp:
      2;
  }


  .description {
    font-size: 10px;

    -webkit-line-clamp:
      3;
  }


  .creator-block {
    margin-top: 15px;

    padding:
      10px;
  }


  .creator-avatar-wrapper,
  .creator-avatar {
    width: 36px;
    height: 36px;
  }


  .card-footer {
    padding-top: 13px;
  }


  .open-question {
    display: none;
  }


  .question-card:hover {
    transform: none;
  }


  .question-card:hover
  .comments {
    opacity: 1;
  }

}


/* ============================================================
   SMALL MOBILE
============================================================ */

@media (max-width: 380px) {

  .question-card {
    padding:
      16px
      14px
      14px;
  }


  .creator-role {
    max-width: 145px;
  }


  .answers-label {
    display: none;
  }


  .comment-count {
    font-size: 6px;
  }

}


/* ============================================================
   REDUCED MOTION
============================================================ */

@media (
  prefers-reduced-motion:
  reduce
) {

  .question-card,
  .question-card::before,
  .like-button,
  .like-button svg,
  .author-chevron,
  .open-question {
    transition: none;
    animation: none;
  }

}

</style>